import {
  Currency,
  DepositPaymentEvent,
  DirtLevelEnum,
  Incoterms,
  MaterialCreationProcess,
  MaterialStorageType,
  PaymentEventEnum,
  PaymentMethodEnum,
  TransportMethodEnum,
} from 'api';
import { MaterialComponentValueSchema } from 'features';
import { createIncotermDependentValidation, yup } from 'form';

import { UploadDocumentSchema } from '~/document';

import { variablePricingSchema } from './variablePricingSchema';

export const selectableIncoterms: Incoterms[] = [
  Incoterms.EXW,
  Incoterms.FCA,
  Incoterms.FAS,
  Incoterms.FOB,
  Incoterms.CFR,
  Incoterms.CIF,
];

export const basePurchaseOrderFormSchema = variablePricingSchema.shape({
  basel_code_override: yup.string().optional(),
  collection_site_id: yup.number().required(),
  comments: yup.string().optional(),
  company_payment_term_id: yup.number().optional(),
  creation_process: yup.string().oneOf(Object.values(MaterialCreationProcess)).optional(),
  delivery_site_id: yup.number().optional(),
  deposit_event_offset: yup.number().optional(),
  deposit_event_offset_direction: yup.number().required(),
  deposit_payment_event: yup.string().oneOf(Object.values(DepositPaymentEvent)).optional(),
  deposit_percentage: yup.string().required(),
  dirt_level: yup
    .string()
    .oneOf(Object.values(DirtLevelEnum))
    .optional()
    .transform((value) => {
      return value === 'n/a' ? undefined : value;
    }),
  documents: yup.array().of(UploadDocumentSchema).optional(),
  due_date_direction: yup.number().required(),
  due_date_offset: yup.number().required(),
  exporter_id: yup.number().optional(),
  external_notes: yup.string().optional(),
  external_reference_number: yup.string().optional(),
  form_id: yup.number().optional(),
  grade_id: yup.number().required(),
  has_caps_and_labels: yup.boolean().optional(),
  icd_id: yup.number().optional(),
  incoterm: yup.string().oneOf(selectableIncoterms).required(),
  internal_notes: yup.string().optional(),
  is_food_grade: yup.boolean().optional(),
  is_internal_grade_shown_to_seller: yup.boolean().optional(),
  is_long_term_contract: yup.boolean().optional(),
  material_colour_id: yup.number().optional(),
  material_id: yup.number().optional(),
  material_product_id: yup.number().optional(),
  material_spec_id: yup.number().required(),
  origin_id: yup.number().optional(),
  payment_before_material_is_released: yup.boolean().required(),
  payment_event: yup.string().oneOf(Object.values(PaymentEventEnum)).required(),
  payment_method: yup.string().oneOf(Object.values(PaymentMethodEnum)).required(),
  payment_terms: yup.string().optional(),
  payment_terms_comments: yup.string().optional(),
  port_of_discharge_id: createIncotermDependentValidation([Incoterms.CFR, Incoterms.CIF]),
  port_of_loading_id: createIncotermDependentValidation([Incoterms.FCA, Incoterms.FAS, Incoterms.FOB]),
  price_per_tonne: yup.string().required(),
  producer_id: yup.number().required(),
  production_site_id: yup.number().required(),
  seller_agent_id: yup.number().when('third-party_commission_per_tonne', {
    is: (commission: string) => commission && Number(commission) > 0,
    otherwise: (schema) => schema.optional(),
    then: (schema) => schema.required(),
  }),
  seller_currency: yup.string().oneOf(Object.values(Currency)).required(),
  seller_id: yup.number().required(),
  seller_material_component_values: yup.array().of(MaterialComponentValueSchema).optional(),
  should_update_material_properties_from_site_material: yup.boolean().optional(),
  site_material_id: yup.number().optional(),
  storage_type: yup.string().oneOf(Object.values(MaterialStorageType)).optional(),
  target_collection_date: yup.string().required(),
  third_party_commission_currency: yup.string().oneOf(Object.values(Currency)).optional(),
  third_party_commission_per_tonne: yup.string().required().default('0'),
  tonnage_variance: yup.string().required(),
  tonnes: yup.string().required(),
  tonnes_per_load: yup.string().required(),
  transport_method: yup.string().oneOf(Object.values(TransportMethodEnum)).required(),
});
